import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, CheckCircle, XCircle, AlertTriangle, Shield, Search, RotateCcw, Loader2 } from "lucide-react";
import { API } from "../../config";

// Outcome → colour + icon mapping. Centralised so additions to the
// backend enum (e.g. ``rate_limited``) only need a single touch-point.
// KEEP IN SYNC WITH `backend/routes/salesforce_dealer_sync.py` (search
// for `outcome=` string literals). If a new outcome appears in the
// backend without a matching entry here, the widget renders the raw
// code + a neutral grey style (via `_defaultMeta` below) instead of
// silently collapsing it into "Validation 4xx" — which used to cause
// two visually identical cards for `invalid_payload` +
// `environment_mismatch` (Fabrizio CR 04/07/2026).
const OUTCOME_META = {
  created:              { color: "text-emerald-400", bg: "bg-emerald-500/10",  border: "border-emerald-500/30", icon: CheckCircle, label: "Creato" },
  updated:              { color: "text-blue-400",    bg: "bg-blue-500/10",     border: "border-blue-500/30",    icon: CheckCircle, label: "Aggiornato" },
  duplicate:            { color: "text-amber-400",   bg: "bg-amber-500/10",    border: "border-amber-500/30",   icon: AlertTriangle, label: "Duplicato" },
  no_changes:           { color: "text-gray-400",    bg: "bg-gray-500/10",     border: "border-gray-500/30",    icon: CheckCircle, label: "Nessuna modifica" },
  environment_mismatch: { color: "text-yellow-300",  bg: "bg-yellow-500/10",   border: "border-yellow-500/30",  icon: AlertTriangle, label: "Info · cross-env" },
  validation_error:     { color: "text-red-400",     bg: "bg-red-500/10",      border: "border-red-500/30",     icon: XCircle, label: "Payload invalido 4xx" },
  auth_error:           { color: "text-red-400",     bg: "bg-red-500/10",      border: "border-red-500/30",     icon: Shield, label: "Auth 401" },
  internal_error:       { color: "text-fuchsia-400", bg: "bg-fuchsia-500/10",  border: "border-fuchsia-500/30", icon: XCircle, label: "Errore server 5xx" },
  server_error:         { color: "text-fuchsia-400", bg: "bg-fuchsia-500/10",  border: "border-fuchsia-500/30", icon: XCircle, label: "Errore server 5xx" },
  not_found:            { color: "text-gray-400",    bg: "bg-gray-500/10",     border: "border-gray-500/30",    icon: XCircle, label: "Non trovato 404" },
};

// Neutral fallback: raw outcome code + grey style. Better than silently
// collapsing new backend enum values into the "Validation 4xx" bucket.
const _prettifyOutcome = (raw) => (raw || "unknown")
  .replace(/_/g, " ")
  .replace(/\b\w/g, (c) => c.toUpperCase());

const _defaultMeta = (raw) => ({
  color: "text-slate-300",
  bg:    "bg-slate-500/10",
  border:"border-slate-500/30",
  icon:  AlertTriangle,
  label: _prettifyOutcome(raw),
});

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "medium" });
  } catch { return iso || "-"; }
};

export default function SalesforceInboundLogsWidget() {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [outcomeFilter, setOutcomeFilter] = useState("");
  const [dealerCodeFilter, setDealerCodeFilter] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  // Fabrizio CR 02/09/2026 — Zorzutti feedback: env-mismatch rows are
  // legitimate safety-net rejections, not real errors — the tab looks
  // scarier than reality. Persist the user's preference locally so
  // opening this tab again keeps the hidden state consistent.
  const [hideEnvMismatch, setHideEnvMismatch] = useState(() => {
    try {
      return localStorage.getItem("sf_inbound_hide_env_mismatch") === "1";
    } catch { return false; }
  });
  const toggleHideEnvMismatch = () => {
    const next = !hideEnvMismatch;
    setHideEnvMismatch(next);
    try { localStorage.setItem("sf_inbound_hide_env_mismatch", next ? "1" : "0"); } catch { /* noop */ }
  };
  // Fabrizio CR 04/07/2026 — one-click env-mismatch resolver.
  // Tracks WHICH log row is currently being replayed so the button
  // shows a spinner + we prevent double-clicks racing the API.
  const [replayingKey, setReplayingKey] = useState(null);

  // Visible rows respect the hide-env-mismatch toggle (client-side
  // filter, not backend — the log stays in DB for audit).
  const visibleLogs = hideEnvMismatch
    ? logs.filter((l) => l.outcome !== "environment_mismatch")
    : logs;
  const hiddenCount = hideEnvMismatch
    ? logs.filter((l) => l.outcome === "environment_mismatch").length
    : 0;

  const replayWithEnv = async (log, forcedEnv) => {
    const key = `${log.dealer_code}::${log.created_at}`;
    if (!window.confirm(
      `Rieseguire questo webhook con ambiente forzato a "${forcedEnv.toUpperCase()}"?\n\n` +
      `Dealer: ${log.dealer_code} (${log.email})\n` +
      `Outcome originale: ${log.outcome} (HTTP ${log.http_status})\n\n` +
      `Se l'ambiente scelto è quello corretto per questo dealer, il record ` +
      `verrà creato normalmente (welcome email inclusa).`
    )) return;
    setReplayingKey(key);
    try {
      const res = await axios.post(
        `${API}/admin/audit/salesforce-sync-logs/replay-with-env-override`,
        {
          dealer_code: log.dealer_code,
          created_at: log.created_at,
          environment: forcedEnv,
        }
      );
      if (res.data.ok) {
        toast.success(
          `Rebind riuscito · nuovo outcome: ${res.data.new_outcome} · ` +
          `${res.data.welcome_email_sent ? "welcome email inviata" : "email non inviata"}`,
          { duration: 7000 }
        );
      } else {
        toast.error(`Retry completato con outcome: ${res.data.new_outcome} — ${res.data.detail || ""}`);
      }
      fetchLogs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante il rebind");
    } finally { setReplayingKey(null); }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "50" });
      if (outcomeFilter) params.set("outcome", outcomeFilter);
      if (dealerCodeFilter.trim()) params.set("dealer_code", dealerCodeFilter.trim());
      const res = await axios.get(`${API}/admin/audit/salesforce-sync-logs?${params}`);
      setLogs(res.data.entries || []);
      setSummary(res.data.summary_by_outcome || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); }, [outcomeFilter]);

  return (
    <div className="space-y-5" data-testid="sf-inbound-logs-widget">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-base font-semibold text-white">Dealer in arrivo da Salesforce</h3>
          <p className="text-xs text-gray-500 mt-1">
            Webhook <code className="text-gray-400 bg-[#0F0F0F] px-1.5 py-0.5 rounded">POST /api/salesforce/dealers/sync</code> · Newest first
          </p>
        </div>
        <button
          onClick={fetchLogs}
          disabled={loading}
          className="text-xs px-3 py-1.5 rounded-md bg-[#1A1A1A] hover:bg-[#252525] border border-gray-700 text-gray-300 inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="sf-inbound-logs-refresh"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          Aggiorna
        </button>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
        {summary.map((s) => {
          const meta = OUTCOME_META[s.outcome] || _defaultMeta(s.outcome);
          const active = outcomeFilter === s.outcome;
          return (
            <button
              key={s.outcome}
              onClick={() => setOutcomeFilter(active ? "" : s.outcome)}
              className={`p-3 rounded-lg border text-left transition-colors ${meta.bg} ${meta.border} ${active ? "ring-2 ring-white/20" : ""}`}
              data-testid={`sf-inbound-summary-${s.outcome}`}
            >
              <div className={`text-[10px] uppercase tracking-wider font-semibold ${meta.color}`}>{meta.label}</div>
              <div className="text-xl font-bold text-white mt-1">{s.count}</div>
            </button>
          );
        })}
        {summary.length === 0 && !loading && (
          <div className="col-span-full text-xs text-gray-500 italic py-2">Nessun dato disponibile</div>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px] relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            value={dealerCodeFilter}
            onChange={(e) => setDealerCodeFilter(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchLogs()}
            placeholder="Cerca per dealer_code esatto"
            className="w-full pl-9 pr-3 py-2 bg-[#0F0F0F] border border-gray-800 rounded-lg text-white text-sm focus:border-blue-600 focus:outline-none"
            data-testid="sf-inbound-dealer-filter"
          />
        </div>
        {outcomeFilter && (
          <button
            onClick={() => setOutcomeFilter("")}
            className="text-xs px-3 py-1.5 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-400"
            data-testid="sf-inbound-clear-filter"
          >
            Filtro attivo: {outcomeFilter} ✕
          </button>
        )}
        {/* Fabrizio CR 02/09/2026 — hide env-mismatch rows (they're
            legitimate safety-net rejections, not real errors — noisy
            for Legal/Ops audience). Persistent via localStorage. */}
        <label className="text-xs inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#0F0F0F] border border-gray-800 text-gray-300 cursor-pointer hover:border-gray-700" data-testid="sf-inbound-hide-envmismatch-toggle">
          <input
            type="checkbox"
            checked={hideEnvMismatch}
            onChange={toggleHideEnvMismatch}
            className="accent-yellow-500 cursor-pointer"
          />
          Nascondi env-mismatch
          {hiddenCount > 0 && (
            <span className="text-yellow-300 font-mono text-[10px] ml-1">({hiddenCount})</span>
          )}
        </label>
        <div className="text-xs text-gray-500">{total} totali</div>
      </div>

      {/* Logs table */}
      <div className="bg-[#0F0F0F] border border-gray-800 rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-[#1A1A1A]">
            <tr className="text-gray-500">
              <th className="text-left py-2.5 px-3 font-semibold">Quando</th>
              <th className="text-left py-2.5 px-3 font-semibold">Outcome</th>
              <th className="text-left py-2.5 px-3 font-semibold">HTTP</th>
              <th className="text-left py-2.5 px-3 font-semibold">Dealer Code</th>
              <th className="text-left py-2.5 px-3 font-semibold">Email</th>
              <th className="text-left py-2.5 px-3 font-semibold">Errore</th>
            </tr>
          </thead>
          <tbody>
            {visibleLogs.map((l, i) => {
              const meta = OUTCOME_META[l.outcome] || _defaultMeta(l.outcome);
              const Icon = meta.icon;
              const isExpanded = expandedId === i;
              // Fabrizio CR 02/09/2026 — env_mismatch is not a "real"
              // error, it's a safety-net rejection. Render its error
              // cell in soft yellow instead of alarming red.
              const errorTextClass = l.outcome === "environment_mismatch"
                ? "text-yellow-300/80"
                : "text-red-400";
              return (
                <>
                  <tr
                    key={i}
                    className={`border-t border-gray-800 hover:bg-[#1A1A1A] cursor-pointer ${isExpanded ? "bg-[#151515]" : ""}`}
                    onClick={() => setExpandedId(isExpanded ? null : i)}
                    data-testid={`sf-inbound-log-row-${i}`}
                  >
                    <td className="py-2.5 px-3 text-gray-400 font-mono whitespace-nowrap">{formatDate(l.created_at)}</td>
                    <td className="py-2.5 px-3">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded ${meta.bg} ${meta.color}`}>
                        <Icon size={11} />{meta.label}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-gray-300 font-mono">{l.http_status}</td>
                    <td className="py-2.5 px-3 text-gray-200 font-mono">{l.dealer_code}</td>
                    <td className="py-2.5 px-3 text-gray-400 truncate max-w-[180px]">{l.email}</td>
                    <td className={`py-2.5 px-3 truncate max-w-[220px] ${errorTextClass}`}>{l.error || "—"}</td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-[#0A0A0A] border-t border-gray-800">
                      <td colSpan={6} className="p-4">
                        <div className="grid grid-cols-2 gap-4 text-[11px]">
                          <div>
                            <div className="text-gray-500 uppercase tracking-wider mb-1.5">Payload ricevuto</div>
                            <pre className="bg-[#000] border border-gray-800 rounded-md p-2 overflow-x-auto text-gray-300 text-[10px] font-mono">
{JSON.stringify(l.payload, null, 2)}
                            </pre>
                          </div>
                          <div className="space-y-2">
                            <div>
                              <div className="text-gray-500 uppercase tracking-wider mb-1.5">Errore completo</div>
                              <div className="bg-[#000] border border-gray-800 rounded-md p-2 text-red-300 font-mono text-[10px]">
                                {l.error || "—"}
                              </div>
                            </div>
                            <div>
                              <div className="text-gray-500 uppercase tracking-wider mb-1.5">IP origine</div>
                              <div className="text-gray-300 font-mono">{l.request_ip || "—"}</div>
                            </div>
                            {(l.outcome === "environment_mismatch" ||
                              l.outcome === "validation_error" ||
                              l.outcome === "internal_error") && !l.replayed_at && (
                              <div className="bg-lojack-clause-500/5 border border-lojack-clause-500/30 rounded-lg p-3" data-testid={`sf-inbound-rebind-panel-${i}`}>
                                <div className="text-lojack-clause-300 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                  <RotateCcw size={11} />
                                  Rebind al business corretto
                                </div>
                                <p className="text-gray-400 text-[10px] leading-relaxed mb-2.5">
                                  Riesegue il webhook con l&apos;ambiente forzato. Utile quando il dealer_code
                                  è arrivato al webhook sbagliato (es. TEST-* inviato in produzione).
                                </p>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); replayWithEnv(l, "production"); }}
                                    disabled={replayingKey !== null}
                                    className="text-[11px] px-2.5 py-1.5 rounded-md bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 font-medium flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid={`sf-inbound-rebind-prod-${i}`}
                                  >
                                    {replayingKey === `${l.dealer_code}::${l.created_at}` ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />}
                                    → Production
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); replayWithEnv(l, "uat"); }}
                                    disabled={replayingKey !== null}
                                    className="text-[11px] px-2.5 py-1.5 rounded-md bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/40 text-amber-200 font-medium flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid={`sf-inbound-rebind-uat-${i}`}
                                  >
                                    {replayingKey === `${l.dealer_code}::${l.created_at}` ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />}
                                    → UAT
                                  </button>
                                </div>
                              </div>
                            )}
                            {l.replayed_at && (
                              <div className="bg-emerald-500/5 border border-emerald-500/30 rounded-lg p-3" data-testid={`sf-inbound-rebind-done-${i}`}>
                                <div className="text-emerald-300 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                                  <CheckCircle size={11} />
                                  Rebind già eseguito
                                </div>
                                <div className="text-gray-400 text-[10px] leading-relaxed">
                                  Forzato a <span className="font-mono text-emerald-300">{l.replayed_with_env}</span> da{" "}
                                  <span className="text-emerald-300">{l.replayed_by}</span> — nuovo outcome:{" "}
                                  <span className="font-mono text-white">{l.replay_outcome}</span>
                                </div>
                                {l.replay_detail && (
                                  <div className="text-gray-500 text-[10px] mt-1 font-mono truncate" title={l.replay_detail}>
                                    {l.replay_detail}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
            {visibleLogs.length === 0 && !loading && (
              <tr><td colSpan={6} className="py-8 text-center text-gray-500 italic">
                {logs.length > 0 && hideEnvMismatch
                  ? `Tutti gli ${hiddenCount} log corrispondenti sono env-mismatch (nascosti dal filtro).`
                  : "Nessun log corrispondente ai filtri."}
              </td></tr>
            )}
            {loading && (
              <tr><td colSpan={6} className="py-8 text-center text-gray-500"><RefreshCw size={14} className="animate-spin inline mr-2" /> Caricamento…</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
